import {client, uploaderClient} from "@/data/axios";

import {ResponseError} from "@/types";
import {resolveError} from "@/data/_resolveError";
import {Photo} from "@/types/PhotoType";
import {UploadingFileInfo} from "@/types/UploadingFileInfoType";

export const fetch = (
    gallery: string,
    folder: string | null = null,
    cluster: string | number | null = null,
    success = (photos: Photo[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    let url = `/photos/${gallery}`;

    if(folder !== null) {
        url = url + `/${folder}`
    }

    const params: Record<string, string> = {};

    if(cluster !== null && cluster !== undefined) {
        params.cluster = String(cluster);
    }

    client.get(url, { params })
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const deleteItem = (
    photo: Number,
    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.delete(`/photos/${photo}/delete`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
};

export const recoverDeletedItem = (
    photo: Number,
    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/photos/${photo}/recover`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
};

export const deleteList = (
    photos: Photo[],
    gallery: string | null,

    success = (deleted: Number[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/photos/delete`, {
        gallery,
        photos: photos.map(item => item.id),
    })
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const recoverDeletedList = (
    photos: Photo[],
    gallery: string | null,

    success = (recovered: Number[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/photos/recover`, {
        gallery,
        photos: photos.map(item => item.id),
    })
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const sortPhotos = (
    photos: Photo[],
    gallery: string,
    type: string,
    folder: string | null,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const sorted = photos.map(item => item.id);

    client.post(`/photos/sort`, {
        sorted,
        gallery,
        folder,
        type
    })
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
};

export const uploadPhoto = async (
    file: File,
    gallery: string,
    folder: string | null,

    success = (photo: Photo) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('gallery', gallery);
    formData.append('filename', file.name);
    formData.append('filesize', file.size.toString());

    if(folder) {
        formData.append('folder', folder);
    }

    await uploaderClient.post(`/upload`, formData)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const getUploadUrl = async (
    file: UploadingFileInfo,
    gallery: string,
    folder: string | null,

    success = (data: {url: string, photo: Photo}) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const formData = new FormData();

    formData.append('gallery', gallery);

    formData.append('filename', file.name);
    formData.append('filesize', file.size.toString());
    formData.append('filetype', file.type || 'application/octet-stream');

    if(folder) {
        formData.append('folder', folder);
    }

    await client.post(`/photos/upload/url`, formData)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const uploadPhotoBySignedUrl = async (
    file: File,
    url: string,

    success = (data: any) => {},
    failed = (type: ResponseError, data: any) => {},
    onProgress: ((loaded: number) => void) | null = null,
    _retryCount = 0
) => {
    const MAX_RETRIES = 2;
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('Authorization', '');

    const STALL_TIMEOUT = 60_000;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const clearStallTimer = () => {
        if (stallTimer) {
            clearTimeout(stallTimer);
            stallTimer = null;
        }
    };

    const resetStallTimer = () => {
        clearStallTimer();
        stallTimer = setTimeout(() => xhr.abort(), STALL_TIMEOUT);
    };

    xhr.upload.onprogress = (event: ProgressEvent) => {
        resetStallTimer();
        if (onProgress && event.lengthComputable) {
            onProgress(event.loaded);
        }
    };
    xhr.onloadstart = () => resetStallTimer();

    xhr.onload = () => {
        clearStallTimer();
        if (xhr.status >= 200 && xhr.status < 300) {
            success({
                status: xhr.status,
                response: xhr.responseText,
                responseURL: xhr.responseURL,
                file: file,
                fileType: file.type,
                fileName: file.name,
                fileSize: file.size,
            });
        } else {
            let responseData;
            try {
                responseData = JSON.parse(xhr.responseText);
            } catch (e) {
                responseData = xhr.responseText || 'Unknown error';
            }

            let errorType = ResponseError.Unknown;
            if (xhr.status === 400) errorType = ResponseError.Validation;
            else if (xhr.status === 401) errorType = ResponseError.Unauthorized;
            else if (xhr.status === 404) errorType = ResponseError.NotFound;
            else if (xhr.status === 413) errorType = ResponseError.TooLarge;
            else if (xhr.status >= 500) errorType = ResponseError.Internal;

            failed(errorType, { status: xhr.status, response: responseData });
            console.error('Upload failed:', xhr.status, responseData);
        }
    };

    xhr.onerror = () => {
        clearStallTimer();
        if (_retryCount < MAX_RETRIES) {
            const delay = 1000 * (_retryCount + 1); // 1s, 2s
            console.warn(`Network error (status 0), retry ${_retryCount + 1}/${MAX_RETRIES} in ${delay}ms`);
            setTimeout(() => {
                uploadPhotoBySignedUrl(file, url, success, failed, onProgress, _retryCount + 1);
            }, delay);
            return;
        }
        failed(ResponseError.Network, {
            status: 0,
        });
        console.error('Network error after all retries:', xhr.status, xhr.statusText);
    };

    xhr.ontimeout = () => {
        clearStallTimer();
        failed(ResponseError.Unknown, { message: 'The request timed out' });
        console.error('Upload request timed out');
    };

    xhr.onabort = () => {
        clearStallTimer();
        failed(ResponseError.Unknown, { message: 'Network connection stalled, please retry' });
        console.error('Upload request aborted (stall timeout or user abort)');
    };

    const isVideo = (file.type || '').startsWith('video/');
    xhr.timeout = isVideo ? 3_600_000 : 600_000;

    try {
        xhr.send(file);
    } catch (error) {
        let errorMessage = 'Unknown error';
        let errorStack = '';

        if (error instanceof Error) {
            errorMessage = error.message;
            errorStack = error.stack || '';
        }

        failed(ResponseError.Unknown, { message: errorMessage, stack: errorStack });
        console.error('Exception occurred while sending the file:', errorMessage, errorStack);
    }
}

export const uploadPhotoSuccess = async (
    photo: Photo,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
)=> {
    await client.post(`/photos/upload/${photo.id}/uploaded`, {
        photo: photo.id,
    })
        .then(() => {
            success();
        }).catch((error) => resolveError(error, failed));
}

export const moveToFolder = (
    photos: Photo[],
    folder: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const toMove = photos.map(item => item.id);

    client.post(`/photos/move/${folder}`, {
        photos: toMove
    })
        .then(({data}: any) => {
            success();
        }).catch((error) => resolveError(error, failed));
};

export const removeFromFolder = (
    photos: Photo[],
    folder: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const toRemove = photos.map(item => item.id);

    client.post(`/photos/remove/${folder}`, {
        photos: toRemove
    })
        .then(({data}: any) => {
            success();
        }).catch((error) => resolveError(error, failed));
};

export const coverPhoto = (
    photo: Photo,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/photos/${photo.id}/cover`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const getProcessedPhotos = async (
    photoIds: number[],
    success = (photos: Photo[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    await client.post(`/photos/processed`, {
        photo_ids: photoIds
    })
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};